import { useMemo } from "react";
import { useWatch, useFormContext } from "react-hook-form";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  useTemplateContext,
  useRecipientSelection,
} from "@/containers/whatsapp-broadcasts/context";
import { TemplatePreview } from "@/components/whatsapp/TemplatePreview";
import BroadcastPreview from "./BroadcastPreview";
import type { BroadcastParameter } from "./utils";
import {
  DEFAULT_FMB_OUTSTANDING_TEMPLATE_NAME,
  DEFAULT_MUTTAVATTEEN_OUTSTANDING_TEMPLATE_NAME,
  DEFAULT_SABIL_OUTSTANDING_TEMPLATE_NAME,
  FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_ONLY,
  SABIL_OUTSTANDING_NOTIFY_HOUSEHOLD_ONLY,
} from "./utils";
import type { FilterGroupData } from "./advanced-filter-builder/constants";
import type { MiqaatRsvpFilterEnvelope } from "./miqaat-rsvp-filter-criteria";
import { describeBroadcastRecipientSource } from "./broadcast-summary";
import { useGetOne, type RaRecord } from "react-admin";

const TABLE_HEAD_CELLS = [
  { label: "#", key: "index" },
  { label: "Phone Number", key: "phone" },
  { label: "ITS ID", key: "itsId" },
  { label: "Full Name", key: "fullName" },
] as const;

const EMPTY_BROADCAST_PARAMETERS = {} as Record<string, BroadcastParameter>;

type SummaryFormData = {
  name?: string;
  templateName?: string;
  parameters?: Record<string, BroadcastParameter>;
  filterCriteria?: FilterGroupData | MiqaatRsvpFilterEnvelope;
};

const SummaryStep = () => {
  const { control } = useFormContext();

  const name = useWatch({ control, name: "name" });
  const templateName = useWatch({ control, name: "templateName" });
  const broadcastKind = useWatch({ control, name: "broadcastKind" }) as string | undefined;
  const sabilType = useWatch({ control, name: "sabilType" }) as string | undefined;
  const miqaatId = useWatch({ control, name: "miqaatId" }) as string | undefined;
  const eventId = useWatch({ control, name: "eventId" }) as string | undefined;
  const hijriYearStart = useWatch({ control, name: "hijriYearStart" }) as number | undefined;
  const minOutstandingPercent = useWatch({ control, name: "minOutstandingPercent" }) as
    | number
    | undefined;
  const thaliTypeIds = useWatch({ control, name: "thaliTypeIds" }) as string[] | undefined;
  const deliveryScheduleProfileIds = useWatch({
    control,
    name: "deliveryScheduleProfileIds",
  }) as string[] | undefined;
  const itsIds = useWatch({ control, name: "itsIds" }) as string[] | undefined;
  const excludeItsIds = useWatch({ control, name: "excludeItsIds" }) as string[] | undefined;
  const recipientItsIds = useWatch({ control, name: "recipientItsIds" }) as string[] | undefined;
  const notifyMode = useWatch({ control, name: "notifyMode" }) as string | undefined;
  const parameters = useWatch({
    control,
    name: "parameters",
    defaultValue: {},
  }) as Record<string, BroadcastParameter>;
  const filterCriteria = useWatch({ control, name: "filterCriteria" }) as
    | FilterGroupData
    | undefined;

  const formData = useMemo(
    (): SummaryFormData => ({
      name,
      templateName,
      parameters,
      filterCriteria,
    }),
    [name, templateName, parameters, filterCriteria]
  );

  return (
    <SummaryContent
      formData={formData}
      broadcastKind={broadcastKind}
      sabilType={sabilType}
      miqaatId={miqaatId}
      eventId={eventId}
      hijriYearStart={hijriYearStart}
      minOutstandingPercent={minOutstandingPercent}
      thaliTypeIds={thaliTypeIds}
      deliveryScheduleProfileIds={deliveryScheduleProfileIds}
      itsIds={itsIds}
      excludeItsIds={excludeItsIds}
      recipientItsIds={recipientItsIds}
      notifyMode={notifyMode}
    />
  );
};

type SummaryContentProps = {
  formData: SummaryFormData;
  broadcastKind?: string;
  sabilType?: string;
  miqaatId?: string;
  eventId?: string;
  hijriYearStart?: number;
  minOutstandingPercent?: number;
  thaliTypeIds?: string[];
  deliveryScheduleProfileIds?: string[];
  itsIds?: string[];
  excludeItsIds?: string[];
  recipientItsIds?: string[];
  notifyMode?: string;
};

const SummaryContent = ({
  formData,
  broadcastKind,
  sabilType,
  miqaatId,
  eventId,
  hijriYearStart,
  minOutstandingPercent,
  thaliTypeIds,
  deliveryScheduleProfileIds,
  itsIds,
  excludeItsIds,
  recipientItsIds,
  notifyMode,
}: SummaryContentProps) => {
  const isMiqaatRsvp = broadcastKind === "MIQAAT_RSVP";
  const isEventRsvp = broadcastKind === "EVENT_RSVP";
  const isFmbOutstanding = broadcastKind === "FMB_OUTSTANDING";
  const isSabilOutstanding = broadcastKind === "SABIL_OUTSTANDING";
  const isMuttavatteenOutstanding = broadcastKind === "MUTTAVATTEEN_OUTSTANDING";
  const { data: miqaatRecord } = useGetOne(
    "miqaats",
    { id: miqaatId ?? "" },
    { enabled: Boolean(isMiqaatRsvp && miqaatId) }
  );
  const { data: eventRecord } = useGetOne(
    "events",
    { id: eventId ?? "" },
    { enabled: Boolean(isEventRsvp && eventId) }
  );
  const templateName = formData?.templateName;
  const parameters = formData?.parameters ?? EMPTY_BROADCAST_PARAMETERS;
  const filterCriteria = formData?.filterCriteria;

  const { recipientCount, recipientDetails, isLoadingDetails } = useRecipientSelection();

  const { data: selectedTemplate, isLoading: templateLoading } = useTemplateContext();

  const { hasFilterCriteria, recipientSource, filterStats } = useMemo(
    () =>
      describeBroadcastRecipientSource({
        broadcastKind,
        filterCriteria,
        recipientItsIds,
      }),
    [broadcastKind, filterCriteria, recipientItsIds]
  );

  const paramKeys = useMemo(
    () =>
      Object.keys(parameters)
        .filter((key) => {
          const param = parameters[key];
          if (!param) return false;

          if (param && typeof param === "object" && param.type) {
            if (param.type === "text") {
              return param.value && String(param.value).trim() !== "";
            }
            if (param.type === "column") {
              return param.column && String(param.column).trim() !== "";
            }
            return false;
          }

          return String(param).trim() !== "";
        })
        .sort((a, b) => parseInt(a, 10) - parseInt(b, 10)),
    [parameters]
  );

  const previewData = useMemo(() => {
    if (!selectedTemplate) return null;

    const data: RaRecord & {
      components?: { type: string }[];
      exampleVariables?: { value: string }[];
    } = {
      ...selectedTemplate,
      components: selectedTemplate.components || [],
    };

    if (paramKeys.length > 0 && data.components) {
      const paramValues = paramKeys.map((key) => {
        const param = parameters[key];
        if (param && typeof param === "object" && param.type) {
          if (param.type === "text") {
            return String(param.value ?? "");
          }
          if (param.type === "column") {
            const label =
              param.columnSource === "csv"
                ? `CSV: ${param.column || ""}`
                : String(param.column || "");
            return `[${label}]`;
          }
          return String(param.value ?? param.column ?? "");
        }
        return String(param ?? "");
      });
      const bodyComponent = data.components.find((c) => c.type === "BODY");
      if (bodyComponent) {
        data.exampleVariables = paramValues
          .filter((v) => v !== "" && v !== undefined && v !== null)
          .map((v) => ({ value: v }));
      }
    }

    return data;
  }, [selectedTemplate, paramKeys, parameters]);

  /** Live miqaat body vars — do not use Meta's frozen sample (e.g. "8th Feb 2026"). */
  const miqaatRsvpPreviewData = useMemo(() => {
    if (!isMiqaatRsvp || !selectedTemplate || !miqaatRecord) return null;
    return {
      ...selectedTemplate,
      components: selectedTemplate.components || [],
      exampleVariables: [
        { value: "Recipient" },
        { value: String(miqaatRecord.name ?? "—") },
        { value: String(miqaatRecord.venue ?? "—") },
        { value: String(miqaatRecord.slotTypeDate ?? "—") },
      ],
    };
  }, [isMiqaatRsvp, selectedTemplate, miqaatRecord]);

  const recipientCountLabel = useMemo(
    () =>
      isMiqaatRsvp || isEventRsvp
        ? `${recipientCount} WhatsApp message${recipientCount !== 1 ? "s" : ""} (RSVP)`
        : isFmbOutstanding
          ? `${recipientCount} WhatsApp message${recipientCount !== 1 ? "s" : ""} (FMB outstanding)`
          : isSabilOutstanding
            ? `${recipientCount} WhatsApp message${recipientCount !== 1 ? "s" : ""} (Sabil outstanding)`
            : isMuttavatteenOutstanding
              ? `${recipientCount} WhatsApp message${recipientCount !== 1 ? "s" : ""} (Mutawatteen outstanding)`
              : `${recipientCount} recipient${recipientCount !== 1 ? "s" : ""}`,
    [
      recipientCount,
      isMiqaatRsvp,
      isEventRsvp,
      isFmbOutstanding,
      isSabilOutstanding,
      isMuttavatteenOutstanding,
    ]
  );

  const fmbNotifyLabel =
    notifyMode === FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_ONLY
      ? "Household contact only"
      : "Household + family";

  const sabilNotifyLabel =
    notifyMode === SABIL_OUTSTANDING_NOTIFY_HOUSEHOLD_ONLY
      ? "Household contact only"
      : "Household + family";

  const muttavatteenNotifyLabel = "Holder WhatsApp only";

  const previewMessage = useMemo(() => {
    if (!templateName) return "Select a template to see preview";
    if (templateLoading) return "Loading template...";
    return "Preview unavailable";
  }, [templateName, templateLoading]);

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Typography variant="h6" gutterBottom>
        Broadcast Summary
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review all selections before creating the broadcast
      </Typography>
      <Grid container spacing={3}>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
          sx={{ minWidth: 0 }}
        >
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Recipients
                </Typography>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}
                >
                  <Chip
                    label={recipientCountLabel}
                    color={recipientCount > 0 ? "success" : "default"}
                    size="small"
                  />
                  <Chip
                    label={`Source: ${recipientSource}`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                </Box>
                {recipientCount === 0 && (
                  <Typography variant="body2" color="error">
                    No recipients selected
                  </Typography>
                )}
                {hasFilterCriteria && filterStats && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    Filter criteria:{" "}
                    {filterStats.label ??
                      `${filterStats.rulesCount} rule(s), ${filterStats.groupsCount} group(s)`}
                  </Typography>
                )}
                {isFmbOutstanding && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    Period: {hijriYearStart ?? "latest"} · Min outstanding:{" "}
                    {minOutstandingPercent ?? 1}% · Notify: {fmbNotifyLabel}
                    {(thaliTypeIds?.length ?? 0) > 0
                      ? ` · Thali types: ${thaliTypeIds!.length}`
                      : ""}
                    {(deliveryScheduleProfileIds?.length ?? 0) > 0
                      ? ` · Schedules: ${deliveryScheduleProfileIds!.length}`
                      : ""}
                    {(itsIds?.length ?? 0) > 0 ? ` · Household ITS: ${itsIds!.length}` : ""}
                  </Typography>
                )}
                {isSabilOutstanding && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    Min outstanding: {minOutstandingPercent ?? 1}% · Notify: {sabilNotifyLabel}
                    {(itsIds?.length ?? 0) > 0 ? ` · Household ITS: ${itsIds!.length}` : ""}
                    {(excludeItsIds?.length ?? 0) > 0
                      ? ` · Excluded: ${excludeItsIds!.length}`
                      : ""}
                  </Typography>
                )}
                {isMuttavatteenOutstanding && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    Min outstanding: {minOutstandingPercent ?? 1}% · Notify:{" "}
                    {muttavatteenNotifyLabel}
                    {(itsIds?.length ?? 0) > 0 ? ` · Holder ITS: ${itsIds!.length}` : ""}
                    {(excludeItsIds?.length ?? 0) > 0
                      ? ` · Excluded: ${excludeItsIds!.length}`
                      : ""}
                  </Typography>
                )}
                {recipientCount > 0 && recipientDetails.length > 0 && (
                  <Box sx={{ mt: 2, width: "100%", minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1 }}
                    >
                      Recipient List ({recipientDetails.length})
                    </Typography>
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{
                        maxHeight: 300,
                        overflowX: "auto",
                        width: "100%",
                      }}
                    >
                      <Table size="small" stickyHeader sx={{ minWidth: 420 }}>
                        <TableHead>
                          <TableRow>
                            {TABLE_HEAD_CELLS.map((cell) => (
                              <TableCell
                                key={cell.key}
                                sx={{
                                  fontWeight: 600,
                                  backgroundColor: "background.paper",
                                }}
                              >
                                {cell.label}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {isLoadingDetails ? (
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                    py: 2,
                                  }}
                                >
                                  <CircularProgress size={20} />
                                  <Typography variant="body2" color="text.secondary">
                                    Loading recipient details...
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ) : (
                            recipientDetails.map((recipient, index) => (
                              <TableRow
                                key={`${recipient.phone}-${index}`}
                                hover
                                sx={{
                                  backgroundColor: recipient.matched
                                    ? "action.selected"
                                    : "transparent",
                                }}
                              >
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>{recipient.phone}</TableCell>
                                <TableCell>{recipient.ITS_ID}</TableCell>
                                <TableCell>{recipient.Full_Name}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
          sx={{ minWidth: 0 }}
        >
          {isMiqaatRsvp || isEventRsvp ? (
            <Stack spacing={2}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {isMiqaatRsvp ? "Miqaat RSVP" : "Event RSVP"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Template: <strong>miqaat_rsvp</strong> (flow)
                  </Typography>
                  {isMiqaatRsvp && (
                    <>
                      <Typography variant="body2">
                        Miqaat:{" "}
                        <strong>
                          {miqaatRecord ? String(miqaatRecord.name ?? "—") : "Loading miqaat…"}
                        </strong>
                      </Typography>
                      {miqaatRecord?.slotTypeDate ? (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Date ({"{{4}}"}): <strong>{String(miqaatRecord.slotTypeDate)}</strong>
                        </Typography>
                      ) : null}
                    </>
                  )}
                  {isEventRsvp && (
                    <Typography variant="body2">
                      Event:{" "}
                      <strong>
                        {eventRecord ? String(eventRecord.name ?? "—") : "Loading event…"}
                      </strong>
                    </Typography>
                  )}
                </CardContent>
              </Card>
              {isMiqaatRsvp && miqaatRsvpPreviewData ? (
                <TemplatePreview formData={miqaatRsvpPreviewData} />
              ) : (
                <BroadcastPreview />
              )}
            </Stack>
          ) : isFmbOutstanding ? (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  FMB Outstanding
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Template: <strong>{DEFAULT_FMB_OUTSTANDING_TEMPLATE_NAME}</strong> (fixed)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ledger amounts are filled server-side per household. Notify mode:{" "}
                  <strong>{fmbNotifyLabel}</strong>
                </Typography>
              </CardContent>
            </Card>
          ) : isSabilOutstanding ? (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Sabil Outstanding — {sabilType === "ESTABLISHMENT" ? "Establishment" : "Chula"}{" "}
                  only
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Template:{" "}
                  <strong>{templateName || DEFAULT_SABIL_OUTSTANDING_TEMPLATE_NAME}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ledger amounts are filled server-side per sabil. Notify mode:{" "}
                  <strong>{sabilNotifyLabel}</strong>
                </Typography>
              </CardContent>
            </Card>
          ) : isMuttavatteenOutstanding ? (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Mutawatteen Outstanding —{" "}
                  {sabilType === "MUTTAVATTEEN_YEARLY" ? "Yearly" : "Monthly"} only
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Template:{" "}
                  <strong>{templateName || DEFAULT_MUTTAVATTEEN_OUTSTANDING_TEMPLATE_NAME}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ledger amounts are filled server-side per sabil. Notify mode:{" "}
                  <strong>{muttavatteenNotifyLabel}</strong>
                </Typography>
              </CardContent>
            </Card>
          ) : templateName && previewData ? (
            <BroadcastPreview />
          ) : (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                  {previewMessage}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SummaryStep;
